import { useState } from 'react';
import { Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { usePortalPublicacao } from '../hooks/usePortalPublicacao';

interface PublicarPortalButtonProps {
    tipo: 'fatura' | 'proposta' | 'contrato';
    documentoId: string;
    numeroDocumento?: string;
    onSuccess?: () => void;
    variant?: 'button' | 'menu-item' | 'icon';
    publicado?: boolean; // Indica se já foi publicado
}

export function PublicarPortalButton({
    tipo,
    documentoId,
    numeroDocumento: _numeroDocumento,
    onSuccess,
    variant = 'button',
    publicado = false,
}: PublicarPortalButtonProps) {
    const { isPublishing, publicarFatura, publicarProposta } = usePortalPublicacao();
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const handlePublicar = async () => {
        setMessage(null);

        const resultado = tipo === 'fatura'
            ? await publicarFatura(documentoId)
            : await publicarProposta(documentoId);

        setMessage({
            type: resultado.sucesso ? 'success' : 'error',
            text: resultado.mensagem,
        });

        if (resultado.sucesso && onSuccess) {
            setTimeout(() => {
                onSuccess();
                setMessage(null);
            }, 2000);
        } else if (!resultado.sucesso) {
            setTimeout(() => setMessage(null), 5000);
        }
    };

    if (variant === 'icon') {
        return (
            <button
                onClick={handlePublicar}
                disabled={isPublishing || publicado}
                className={`p-2 rounded-lg transition-colors ${
                    publicado
                        ? 'text-green-600 dark:text-green-400 bg-green-500/10 cursor-default'
                        : 'text-purple-600 dark:text-purple-400 hover:bg-purple-500/10'
                } disabled:opacity-50`}
                title={publicado ? 'Publicado no Portal' : 'Publicar no Portal'}
            >
                {isPublishing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : publicado ? (
                    <CheckCircle className="w-4 h-4" />
                ) : (
                    <Send className="w-4 h-4" />
                )}
            </button>
        );
    }

    if (variant === 'menu-item') {
        return (
            <>
                <button
                    onClick={handlePublicar}
                    disabled={isPublishing}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted rounded-md transition-colors disabled:opacity-50"
                >
                    {isPublishing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Send className="h-4 w-4" />
                    )}
                    <span>Publicar no Portal</span>
                </button>
                {message && (
                    <div className={`mt-2 px-3 py-2 text-xs rounded-md ${
                        message.type === 'success'
                            ? 'bg-green-500/20 text-green-700 dark:text-green-400'
                            : 'bg-red-500/20 text-red-700 dark:text-red-400'
                    }`}>
                        {message.text}
                    </div>
                )}
            </>
        );
    }

    return (
        <div className="space-y-2">
            <button
                onClick={handlePublicar}
                disabled={isPublishing}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {isPublishing ? (
                    <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Publicando...</span>
                    </>
                ) : (
                    <>
                        <Send className="h-4 w-4" />
                        <span>Publicar no Portal</span>
                    </>
                )}
            </button>

            {message && (
                <div className={`flex items-center gap-2 px-4 py-3 rounded-md ${
                    message.type === 'success'
                        ? 'bg-green-500/20 text-green-700 dark:text-green-400'
                        : 'bg-red-500/20 text-red-700 dark:text-red-400'
                }`}>
                    {message.type === 'success' ? (
                        <CheckCircle className="h-4 w-4" />
                    ) : (
                        <AlertCircle className="h-4 w-4" />
                    )}
                    <span className="text-sm">{message.text}</span>
                </div>
            )}
        </div>
    );
}
